// Fase 5 · Registro de la "acción de usuario" en curso.
//
// Cada click (o atajo de teclado) abre una acción con un id. Las peticiones
// HTTP que salgan mientras la acción está viva llevan `X-Action-Id`,
// `X-Action-Name` y `X-Screen`, así el panel de depuración une
// click → peticiones → eventos en tiempo real → reacciones de otros dispositivos.
//
// Vida de una acción: ~2 s desde el click, prorrogada mientras tenga
// peticiones en vuelo (las peticiones encadenadas —cobro y luego refetch—
// heredan el mismo id). Coste con el monitor apagado: un id por click y
// tres cabeceras por petición; nada de Redux.

import { screenForPath } from "./debugScreen";

export const ACTION_WINDOW_MS = 2000;
export const ACTION_SETTLE_GRACE_MS = 300;

let current = null; // { id, name, startedAt, expiresAt, pending, meta }
let routeScreen = "public";
const overlays = []; // pila de pantallas superpuestas (p. ej. "tpv.payment")

let clock = () => Date.now();
export function __setClockForTests(fn) {
  clock = fn || (() => Date.now());
}
export function __resetForTests() {
  current = null;
  routeScreen = "public";
  overlays.length = 0;
}

function newId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Pantalla ──────────────────────────────────────────────────────────

export function setRoutePath(pathname) {
  routeScreen = screenForPath(pathname);
}

export function setScreenOverlay(key) {
  if (key) overlays.push(key);
}

export function clearScreenOverlay(key) {
  const i = overlays.lastIndexOf(key);
  if (i >= 0) overlays.splice(i, 1);
}

export function getScreen() {
  return overlays.length ? overlays[overlays.length - 1] : routeScreen;
}

// ── Acciones ──────────────────────────────────────────────────────────

export function beginAction(name, meta = {}) {
  const now = clock();
  current = {
    id: newId(),
    name: String(name || "action").slice(0, 80),
    startedAt: now,
    expiresAt: now + ACTION_WINDOW_MS,
    pending: 0,
    meta,
  };
  return current.id;
}

function isAlive(a, now) {
  return !!a && (a.pending > 0 || now <= a.expiresAt);
}

export function getCurrentAction() {
  const now = clock();
  return isAlive(current, now) ? current : null;
}

/** Llamado al salir una petición: la engancha a la acción viva (si hay). */
export function actionForRequest() {
  const a = getCurrentAction();
  if (!a) return null;
  a.pending += 1;
  return a;
}

/** Llamado al terminar esa petición (éxito o error). */
export function settleRequest(action) {
  if (!action) return;
  action.pending = Math.max(0, action.pending - 1);
  if (action.pending === 0) {
    action.expiresAt = Math.max(action.expiresAt, clock() + ACTION_SETTLE_GRACE_MS);
  }
}

// ── Cabeceras ─────────────────────────────────────────────────────────

// Solo ASCII imprimible. El navegador acepta ISO-8859-1 en las cabeceras,
// pero Kestrel rechaza con 400 (antes de CORS) cualquier valor no ASCII:
// el navegador lo ve como error CORS y la petición falla. Pasaba al pulsar
// botones con tildes ("Cliente fidelización" → la búsqueda siguiente
// fallaba). Las tildes se quitan (á → a, ñ → n) y el resto se descarta.
export function toHeaderSafe(value, max = 120) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x20-\x7E]/g, "")
    .slice(0, max);
}

/** true si la URL va a nuestra API (relativa o bajo baseURL). */
export function isOwnApiUrl(url, baseURL) {
  if (!url) return true;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !url.startsWith("//")) return true;
  if (!baseURL) return false;
  try {
    const u = new URL(url, "http://x");
    const b = new URL(baseURL, "http://x");
    return u.origin === b.origin;
  } catch {
    return false;
  }
}

/**
 * Añade X-Action-Id / X-Action-Name / X-Screen a la petición (solo a nuestra
 * API) y deja la acción en `config.metadata.debug` para enlazar la fila HTTP
 * del monitor. No pisa cabeceras que el caller ya haya puesto.
 */
export function attachDebugHeaders(config, baseURL) {
  if (!config || !isOwnApiUrl(config.url, baseURL ?? config.baseURL)) return config;
  const action = actionForRequest();
  const screen = getScreen();
  config.headers = config.headers || {};
  if (action && !config.headers["X-Action-Id"]) {
    config.headers["X-Action-Id"] = action.id;
    config.headers["X-Action-Name"] = toHeaderSafe(action.name);
  }
  if (!config.headers["X-Screen"]) config.headers["X-Screen"] = toHeaderSafe(screen, 60);
  config.metadata = config.metadata || {};
  config.metadata.debug = {
    action,
    actionId: action?.id || null,
    actionName: action?.name || null,
    screen,
  };
  return config;
}

// ── Click → etiqueta y destino ────────────────────────────────────────

const ACTIONABLE_SELECTOR =
  "[data-trace],button,a,[role='button'],[role='menuitem'],[role='tab'],[role='switch'],[role='checkbox'],[role='option'],input[type='checkbox'],input[type='radio'],.MuiButtonBase-root,.MuiChip-root,.MuiListItem-button";

export function findActionable(el) {
  let node = el;
  while (node && node.nodeType !== 1) node = node.parentNode;
  if (!node || typeof node.closest !== "function") return null;
  return node.closest(ACTIONABLE_SELECTOR);
}

function muiComponentName(el) {
  const cls = typeof el.className === "string" ? el.className : el.getAttribute?.("class") || "";
  const m = cls.match(/Mui([A-Z][A-Za-z]+)-root/);
  return m ? m[1] : null;
}

function visibleText(el) {
  const t = (el.innerText != null ? el.innerText : el.textContent) || "";
  return t.replace(/\s+/g, " ").trim();
}

export function labelFor(el) {
  if (!el) return "unknown";
  const trace = el.getAttribute?.("data-trace");
  if (trace) return trace.slice(0, 40);
  const aria = el.getAttribute?.("aria-label") || el.getAttribute?.("title");
  if (aria) return aria.trim().slice(0, 40);
  const text = visibleText(el);
  if (text) return text.slice(0, 40);
  return muiComponentName(el) || (el.tagName || "el").toLowerCase();
}

function scopeFor(el, screen) {
  const scoped = el.closest?.("[data-trace-scope]");
  if (scoped) return scoped.getAttribute("data-trace-scope");
  if (screen === "tpv.payment" && el.closest?.("[role='dialog']")) return "PaymentDialog";
  const dialog = el.closest?.("[role='dialog']");
  if (dialog) {
    const titleId = dialog.getAttribute("aria-labelledby");
    const title = titleId && dialog.ownerDocument?.getElementById(titleId);
    const t = title ? visibleText(title).slice(0, 30) : "";
    return t ? `Dialog(${t})` : "Dialog";
  }
  return screen;
}

export function targetFor(el, screen) {
  if (!el) return null;
  const tag = (el.tagName || "el").toLowerCase();
  const kind = el.getAttribute?.("role") || muiComponentName(el)?.toLowerCase() || tag;
  return `${scopeFor(el, screen)} > ${kind}[${labelFor(el)}]`.slice(0, 200);
}

export function normalizePoint(clientX, clientY, vw, vh) {
  const w = Math.max(1, Number(vw) || 1);
  const h = Math.max(1, Number(vh) || 1);
  const clamp = (v) => Math.min(1, Math.max(0, v));
  return {
    x: Math.round(clamp((Number(clientX) || 0) / w) * 10000) / 10000,
    y: Math.round(clamp((Number(clientY) || 0) / h) * 10000) / 10000,
    viewportW: Math.round(w),
    viewportH: Math.round(h),
  };
}

/** Datos del click para el recorder; null si no es un elemento accionable. */
export function describeClick(event, win = typeof window !== "undefined" ? window : undefined) {
  const el = findActionable(event?.target);
  if (!el) return null;
  if (el.hasAttribute?.("data-trace-ignore")) return null;
  const screen = getScreen();
  const label = labelFor(el);
  return {
    name: `click:${label}`,
    screen,
    target: targetFor(el, screen),
    ...normalizePoint(event.clientX, event.clientY, win?.innerWidth, win?.innerHeight),
  };
}

/**
 * Clic fuera de cualquier elemento accionable (p. ej. una mesa o una card con
 * `onClick` en un `div`): no abre acción, pero se registra para el mapa de
 * clics (2026-10-05: el plano de mesas y las cards de producto son `div` y el
 * mapa salía vacío). Etiqueta: el `data-trace`/`data-testid` más cercano.
 */
export function describeRawClick(event, win = typeof window !== "undefined" ? window : undefined) {
  let node = event?.target;
  while (node && node.nodeType !== 1) node = node.parentNode;
  if (!node || typeof node.closest !== "function") return null;
  if (node.closest("[data-trace-ignore]")) return null;
  const screen = getScreen();
  const labelled = node.closest("[data-trace],[data-testid]");
  const label = labelled
    ? (labelled.getAttribute("data-trace") || labelled.getAttribute("data-testid")).slice(0, 40)
    : (node.tagName || "el").toLowerCase();
  return {
    name: `click:${label}`,
    screen,
    target: `${screen} > area[${label}]`.slice(0, 200),
    ...normalizePoint(event.clientX, event.clientY, win?.innerWidth, win?.innerHeight),
  };
}

// ── Listener global ───────────────────────────────────────────────────

let installed = false;
let onClickRecord = null;

/**
 * Instala UN listener delegado en `document` (fase de captura, pasivo).
 * `onClick(info, actionId)` se llama tras abrir la acción (el recorder
 * decide si graba según el monitor).
 */
export function installClickTracing(onClick) {
  onClickRecord = onClick || null;
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener(
    "click",
    (event) => {
      try {
        const info = describeClick(event);
        if (!info) {
          // Sin acción: solo punto para el mapa de clics.
          const raw = describeRawClick(event);
          if (raw && onClickRecord) onClickRecord(raw, null);
          return;
        }
        const id = beginAction(info.name, { screen: info.screen });
        if (onClickRecord) onClickRecord(info, id);
      } catch { /* el tracing nunca rompe un click */ }
    },
    { capture: true, passive: true },
  );
}
